import { PlayerNote, SongTrack } from '~/interfaces';
import {
  bestQualityByNote,
  calculateNoteStats,
  NoteScope,
  notesInScope,
  QUALITY_WEIGHTS,
} from '~/modules/game-engine/game-state/helpers/calculate-note-stats';

export interface AccuracyPoint {
  // Where in the song this point sits, 0-1.
  at: number;
  // The windowed mark, 0-1, on the same scale the accuracy is weighed on.
  value: number;
}

export interface AccuracyOverTime {
  // One entry per unbroken stretch the singer owned. Empty means there is nothing to draw.
  segments: AccuracyPoint[][];
  // The figure the stats row prints, so a rule drawn at it really is their own average.
  average: number;
}

// Notes rather than seconds. Note density runs several-fold between a ballad and a patter verse, and
// a window measured in time makes the ballad unreadably jumpy. Eight notes is about one sung line,
// so a dip reads as a line the singer lost rather than a note they fluffed.
const DEFAULT_WINDOW = 8;

// An average of one or two notes is noise rather than form, so nothing is plotted until the window
// holds this many.
const MIN_MARKS = 3;

// A stretch of song this long with nothing of theirs in it - an instrumental, a long rest - breaks
// the line. The window either side of one describes two different parts of the song, and joining
// them draws a slope nobody sang.
const GAP_SHARE = 0.07;

/**
 * How the singer's accuracy moved through the song: a rolling window of the same weighted mark the
 * stats are counted on, placed at each note's position in the song rather than at its index.
 *
 * Broken into segments rather than drawn as one line, because a singer is not answerable for the
 * whole song: pass the mic hands the track over, and the silence where somebody else sang is not a
 * run of zeroes. The gaps are decided here so that nothing drawing this has to know what one means.
 */
export const accuracyOverTime = (
  playerNotes: PlayerNote[],
  track: SongTrack | undefined,
  tolerance: number,
  scope: NoteScope = {},
  { window: size = DEFAULT_WINDOW }: { window?: number } = {},
): AccuracyOverTime => {
  if (!track) return { segments: [], average: 0 };

  // Measured against the whole track rather than this singer's share of it: `at` places a point in
  // the song, so in pass the mic their stretches have to land where their turns actually were.
  const songNotes = notesInScope(track);
  const songEnd = songNotes.reduce((end, note) => Math.max(end, note.start + note.length), 0);

  if (songEnd <= 0) return { segments: [], average: 0 };

  const theirs = new Set(notesInScope(track, scope).map((note) => note.start));
  const { best } = bestQualityByNote(playerNotes, tolerance);

  const segments: AccuracyPoint[][] = [];
  let points: AccuracyPoint[] = [];
  let marks: number[] = [];
  let previousAt: number | null = null;

  // A single point draws nothing - a line needs two ends - so a stretch that only managed one is
  // dropped rather than left to render as an invisible path.
  const endSegment = () => {
    if (points.length > 1) segments.push(points);

    points = [];
    marks = [];
    previousAt = null;
  };

  for (const note of songNotes) {
    if (!theirs.has(note.start)) {
      endSegment();
      continue;
    }

    const at = note.start / songEnd;

    if (previousAt !== null && at - previousAt > GAP_SHARE) endSegment();

    marks.push(QUALITY_WEIGHTS[best.get(note.start) ?? 'missed']);
    if (marks.length > size) marks.shift();
    previousAt = at;

    if (marks.length >= MIN_MARKS) {
      points.push({ at, value: marks.reduce((total, mark) => total + mark, 0) / marks.length });
    }
  }

  endSegment();

  // Read back off the stats rather than added up here, so the rule the graph draws and the figure
  // printed two rows above it cannot drift apart.
  return { segments, average: calculateNoteStats(playerNotes, track, tolerance, scope).accuracy };
};
